import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import ExportarBoton from "../ExportarBoton"
import { formatCurrency, formatNumber, formatPercent } from "../helpers"

const cellSticky = {
  padding: "12px 14px",
  fontSize: 12,
  fontWeight: 600,
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: "1px solid #e2e8f0",
  background: "#f1f5f9",
  position: "sticky",
  top: 0,
  zIndex: 1,
  whiteSpace: "nowrap",
}

const HeaderSort = ({ campo, orden, onClick, align = "left", children }) => {
  const activo = orden.campo === campo
  return (
    <th
      onClick={() => onClick(campo)}
      style={{
        ...cellSticky,
        textAlign: align,
        cursor: "pointer",
        userSelect: "none",
        color: activo ? "#0f172a" : "#475569",
      }}
    >
      {children}
      <i
        className={`mdi mdi-${
          activo ? (orden.asc ? "chevron-up" : "chevron-down") : "unfold-more-horizontal"
        } ms-1`}
        style={{ opacity: activo ? 1 : 0.4, fontSize: 14 }}
      />
    </th>
  )
}

HeaderSort.propTypes = { campo: PropTypes.string, orden: PropTypes.object, onClick: PropTypes.func, align: PropTypes.string, children: PropTypes.node }

const ORDEN_CAMPOS = {
  sucursal: s => (s.sucursal || "").toLowerCase(),
  total_documentos: s => Number(s.total_documentos) || 0,
  subtotal: s => Number(s.subtotal) || 0,
  impuestos: s => Number(s.impuestos?.total_impuestos) || 0,
  propinas: s => Number(s.propinas) || 0,
  total: s => Number(s.total) || 0,
  ticket_promedio: (s, kpiBySuc) => Number(kpiBySuc.get(s.sucursal)?.ticket_promedio) || 0,
}

const SucursalesTab = ({ data, exportOpts }) => {
  const sucursales = data?.sucursales || []
  const kpis = data?.kpis?.sucursales || []
  const [expandidas, setExpandidas] = useState(() => new Set())
  const [busqueda, setBusqueda] = useState("")
  const [orden, setOrden] = useState({ campo: "total", asc: false })

  const kpiBySuc = useMemo(() => {
    const map = new Map()
    kpis.forEach(k => map.set(k.sucursal, k))
    return map
  }, [kpis])

  const toggleOrden = campo => {
    setOrden(prev => {
      if (prev.campo !== campo) return { campo, asc: campo === "sucursal" }
      if (prev.asc && campo !== "sucursal") return { campo, asc: false }
      if (!prev.asc) return { campo, asc: true }
      return { campo, asc: !prev.asc }
    })
  }

  const filtradas = useMemo(() => {
    const q = busqueda.toLowerCase()
    const base = sucursales.filter(s =>
      !q || (s.sucursal || "").toLowerCase().includes(q),
    )
    if (!orden.campo) return base
    const getter = ORDEN_CAMPOS[orden.campo]
    if (!getter) return base
    return [...base].sort((a, b) => {
      const av = getter(a, kpiBySuc)
      const bv = getter(b, kpiBySuc)
      if (typeof av === "string") return orden.asc ? av.localeCompare(bv) : bv.localeCompare(av)
      return orden.asc ? av - bv : bv - av
    })
  }, [sucursales, busqueda, orden, kpiBySuc])

  const toggle = nombre => {
    setExpandidas(prev => {
      const next = new Set(prev)
      if (next.has(nombre)) next.delete(nombre)
      else next.add(nombre)
      return next
    })
  }

  if (sucursales.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-store-off" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin sucursales en el período consultado.</div>
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h6 className="mb-0" style={{ color: "#0f172a", fontWeight: 600 }}>
          Sucursales ({filtradas.length})
        </h6>
        <div style={{ position: "relative", maxWidth: 280 }}>
          <i className="mdi mdi-magnify" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Buscar sucursal…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{ paddingLeft: 32, minWidth: 240 }}
          />
        </div>
      </div>

      <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
        <div style={{ maxHeight: 580, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...cellSticky, width: 40 }} />
                <HeaderSort campo="sucursal" orden={orden} onClick={toggleOrden}>Sucursal</HeaderSort>
                <HeaderSort campo="total_documentos" orden={orden} onClick={toggleOrden} align="right">Docs</HeaderSort>
                <HeaderSort campo="subtotal" orden={orden} onClick={toggleOrden} align="right">Subtotal</HeaderSort>
                <HeaderSort campo="impuestos" orden={orden} onClick={toggleOrden} align="right">Impuestos</HeaderSort>
                <HeaderSort campo="propinas" orden={orden} onClick={toggleOrden} align="right">Propinas</HeaderSort>
                <HeaderSort campo="total" orden={orden} onClick={toggleOrden} align="right">Total</HeaderSort>
                <HeaderSort campo="ticket_promedio" orden={orden} onClick={toggleOrden} align="right">Ticket prom.</HeaderSort>
                <th style={{ ...cellSticky, textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(s => {
                const expandida = expandidas.has(s.sucursal)
                const kpi = kpiBySuc.get(s.sucursal) || {}
                return (
                  <React.Fragment key={s.sucursal}>
                    <tr
                      style={{
                        background: expandida ? "#eff6ff" : "#fff",
                        borderBottom: "1px solid #f1f5f9",
                        cursor: "pointer",
                      }}
                      onClick={() => toggle(s.sucursal)}
                    >
                      <td style={{ padding: "12px 14px", textAlign: "center", color: "#64748b" }}>
                        <i className={`mdi mdi-chevron-${expandida ? "down" : "right"}`} style={{ fontSize: 18 }} />
                      </td>
                      <td style={{ padding: "12px 14px", color: "#0f172a", fontWeight: 600 }}>
                        <i className="mdi mdi-store text-primary me-2" />
                        {s.sucursal}
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right" }}>
                        <span style={{ background: "#e0e7ff", color: "#3730a3", padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                          {formatNumber(s.total_documentos)}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right", color: "#10b981", fontWeight: 600 }}>{formatCurrency(s.subtotal)}</td>
                      <td style={{ padding: "12px 14px", textAlign: "right", color: "#f59e0b", fontWeight: 600 }}>{formatCurrency(s.impuestos?.total_impuestos)}</td>
                      <td style={{ padding: "12px 14px", textAlign: "right", color: "#06b6d4", fontWeight: 600 }}>{formatCurrency(s.propinas)}</td>
                      <td style={{ padding: "12px 14px", textAlign: "right", color: "#0f172a", fontWeight: 700, fontSize: 14 }}>{formatCurrency(s.total)}</td>
                      <td style={{ padding: "12px 14px", textAlign: "right", color: "#475569" }}>{formatCurrency(kpi.ticket_promedio)}</td>
                      <td style={{ padding: "10px 14px", textAlign: "center" }} onClick={e => e.stopPropagation()}>
                        <ExportarBoton exportOpts={exportOpts} sucursal={s.sucursal} iconOnly />
                      </td>
                    </tr>

                    {expandida && (
                      <tr style={{ background: "#fafafa" }}>
                        <td />
                        <td colSpan={8} style={{ padding: "16px 20px" }}>
                          {/* KPIs de la sucursal */}
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 16 }}>
                            {[
                              { l: "Ticket promedio", v: formatCurrency(kpi.ticket_promedio) },
                              { l: "Ticket mediano", v: formatCurrency(kpi.ticket_mediano) },
                              { l: "% Notas crédito", v: formatPercent(kpi.pct_notas_credito ?? 0) },
                              { l: "Ticket máx.", v: formatCurrency(kpi.ticket_maximo) },
                              { l: "Ticket mín.", v: formatCurrency(kpi.ticket_minimo) },
                            ].map((k, i) => (
                              <div key={i} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px" }}>
                                <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>{k.l}</div>
                                <div style={{ fontSize: 14, color: "#0f172a", fontWeight: 700, marginTop: 2 }}>{k.v}</div>
                              </div>
                            ))}
                          </div>

                          {/* Detalle diario */}
                          {Array.isArray(s.acumulado_diario) && s.acumulado_diario.length > 0 && (
                            <>
                              <h6 style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 8 }}>
                                <i className="mdi mdi-calendar me-1" />
                                Detalle diario ({s.acumulado_diario.length} días)
                              </h6>
                              <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                  <thead style={{ position: "sticky", top: 0, background: "#f8fafc" }}>
                                    <tr>
                                      <th style={{ padding: "6px 10px", textAlign: "left", color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>Fecha</th>
                                      <th style={{ padding: "6px 10px", textAlign: "right", color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>Docs</th>
                                      <th style={{ padding: "6px 10px", textAlign: "right", color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>Subtotal</th>
                                      <th style={{ padding: "6px 10px", textAlign: "right", color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {s.acumulado_diario.map((d, i) => (
                                      <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                        <td style={{ padding: "6px 10px", color: "#0f172a" }}>{d.fecha}</td>
                                        <td style={{ padding: "6px 10px", textAlign: "right", color: "#475569" }}>{formatNumber(d.documentos || d.total_documentos)}</td>
                                        <td style={{ padding: "6px 10px", textAlign: "right", color: "#475569" }}>{formatCurrency(d.subtotal)}</td>
                                        <td style={{ padding: "6px 10px", textAlign: "right", color: "#0f172a", fontWeight: 600 }}>{formatCurrency(d.total)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

SucursalesTab.propTypes = {
  data: PropTypes.object,
  exportOpts: PropTypes.object.isRequired,
}

export default SucursalesTab
